import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { DEFAULT_ATTENDANCE_SETTINGS, subscribeAttendanceSettings } from "../services/settingsService";
import { subscribeToClients } from "../services/clientService";
import { createAttendanceRecord, subscribeMyAttendance, subscribeTodayAttendance } from "../services/attendanceService";
import { calculateDistanceMeters, getCurrentPosition, getGeolocationErrorMessage, isValidCoordinate } from "../utils/geolocation";

const initialNotice = { type: "", title: "", message: "" };

export default function Attendance() {
  const { user, userData } = useAuth();
  const [todayRecord, setTodayRecord] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [locationChoice, setLocationChoice] = useState("");
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_ATTENDANCE_SETTINGS);
  const [isMarking, setIsMarking] = useState(false);
  const [progressText, setProgressText] = useState("");
  const [notice, setNotice] = useState(initialNotice);

  const today = new Date().toISOString().slice(0, 10);
  const currentMonth = today.slice(0, 7);
  const activeOffices = useMemo(() => (settings.offices || []).filter((office) => office.active !== false && office.name), [settings.offices]);
  const activeClients = useMemo(() => clients.filter((client) => String(client.status || "Active").toLowerCase() !== "inactive").sort((a, b) => String(a.name).localeCompare(String(b.name))), [clients]);

  useEffect(() => subscribeAttendanceSettings(setSettings, (error) => setNotice({ type: "error", title: "Settings could not be loaded", message: error.message || "Please refresh and try again." })), []);
  useEffect(() => subscribeToClients(setClients, console.error), []);

  useEffect(() => {
    if (!locationChoice) {
      if (settings.allowOffice !== false && activeOffices.length) setLocationChoice(`office:${activeOffices[0].id}`);
      else if (settings.allowClient !== false) setLocationChoice("client");
    }
  }, [settings.allowOffice, settings.allowClient, activeOffices, locationChoice]);

  useEffect(() => {
    if (!user) return undefined;
    return subscribeTodayAttendance(user.uid, today, setTodayRecord);
  }, [user, today]);

  useEffect(() => {
    if (!user) return undefined;
    return subscribeMyAttendance(user.uid, setAttendance);
  }, [user]);

  const showError = (title, message) => setNotice({ type: "error", title, message });
  const showSuccess = (title, message) => setNotice({ type: "success", title, message });

  const markAttendance = async () => {
    if (!user || isMarking) return;
    setNotice(initialNotice);
    if (todayRecord) return showError("Attendance already marked", "Your attendance has already been marked for today.");
    if (!locationChoice) return showError("Select attendance location", "Choose an office or Client Visit before marking attendance.");

    const isClientVisit = locationChoice === "client";
    const selectedOffice = !isClientVisit ? activeOffices.find((office) => `office:${office.id}` === locationChoice) : null;
    const selectedClient = isClientVisit ? activeClients.find((client) => client.id === clientId) : null;
    if (isClientVisit && !selectedClient) return showError("Select client", "Select the client from Client Master before marking attendance.");
    if (!isClientVisit && !selectedOffice) return showError("Office unavailable", "The selected office is no longer active. Select another attendance location.");

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const [cutoffHour, cutoffMinute] = String(settings.halfDayCutoffTime || "12:30").split(":").map(Number);
    if (settings.attendanceCloseTime) {
      const [closeHour, closeMinute] = settings.attendanceCloseTime.split(":").map(Number);
      if (currentMinutes > closeHour * 60 + closeMinute) return showError("Attendance marking is closed", "Attendance marking is closed for today.");
    }
    const status = currentMinutes < cutoffHour * 60 + cutoffMinute ? "Full Day" : "Half Day";

    setIsMarking(true);
    try {
      const targetLatitude = isClientVisit ? selectedClient.latitude : selectedOffice.latitude;
      const targetLongitude = isClientVisit ? selectedClient.longitude : selectedOffice.longitude;
      const targetHasCoordinates = isValidCoordinate(targetLatitude, targetLongitude);
      const geoValidationRequired = Boolean(settings.geoAttendanceEnabled && (!isClientVisit || targetHasCoordinates));
      const allowedRadiusMeters = Number((isClientVisit ? selectedClient.geoRadiusMeters : selectedOffice.radiusMeters) || settings.geoAccuracyMeters || 500);

      let geoFields = {
        geoValidationRequired,
        geoValidated: false,
        geoValidationNote: "Geo-attendance disabled",
        allowedRadiusMeters: geoValidationRequired ? allowedRadiusMeters : null,
        markedLatitude: null,
        markedLongitude: null,
        gpsAccuracyMeters: null,
        distanceMeters: null,
      };

      if (settings.geoAttendanceEnabled && isClientVisit && !targetHasCoordinates) {
        geoFields.geoValidationNote = "Client coordinates not configured";
      } else if (geoValidationRequired) {
        setProgressText("Detecting your location…");
        let position;
        try { position = await getCurrentPosition(); }
        catch (error) { showError("Location could not be verified", getGeolocationErrorMessage(error)); return; }

        const gpsAccuracy = Number(position.coords.accuracy || 0);
        const maximumAccuracy = Number(settings.maxAcceptedGpsAccuracyMeters || 150);
        if (gpsAccuracy > maximumAccuracy) {
          showError("Unable to verify your location accurately", `Your current GPS accuracy is ±${Math.round(gpsAccuracy)} metres. Move near a window or open area and try again.`);
          return;
        }

        const distanceMeters = calculateDistanceMeters(position.coords.latitude, position.coords.longitude, targetLatitude, targetLongitude);
        if (distanceMeters > allowedRadiusMeters) {
          const label = isClientVisit ? selectedClient.name : selectedOffice.name;
          showError("Attendance not marked", `You are approximately ${Math.round(distanceMeters)} metres away from ${label}. Attendance can be marked only within the permitted ${Math.round(allowedRadiusMeters)}-metre radius.`);
          return;
        }

        geoFields = {
          ...geoFields,
          geoValidated: true,
          geoValidationNote: "Location verified",
          markedLatitude: Number(position.coords.latitude),
          markedLongitude: Number(position.coords.longitude),
          gpsAccuracyMeters: Math.round(gpsAccuracy),
          distanceMeters: Math.round(distanceMeters),
        };
      }

      setProgressText("Saving attendance…");
      const locationLabel = isClientVisit ? "Client Visit" : selectedOffice.name;
      await createAttendanceRecord({
        userId: user.uid,
        username: userData?.name || userData?.username || userData?.email,
        date: today,
        status,
        location: locationLabel,
        locationType: isClientVisit ? "Client" : "Office",
        officeId: selectedOffice?.id || "",
        officeName: selectedOffice?.name || "",
        clientId: selectedClient?.id || "",
        clientName: selectedClient?.name || "",
        markedAt: now.toISOString(),
        ...geoFields,
      });

      const detail = geoFields.geoValidated
        ? `${locationLabel} · ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · Within permitted location`
        : isClientVisit && !targetHasCoordinates && settings.geoAttendanceEnabled
          ? `${selectedClient.name} · Client coordinates are not configured, so geo-verification was not required.`
          : `${locationLabel} · ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
      showSuccess("Attendance marked successfully", detail);
    } catch (error) {
      console.error("Attendance save failed:", error);
      showError("Attendance was not saved", navigator.onLine === false ? "Check your internet connection and try again." : "Please try again. If the problem continues, contact Admin.");
    } finally {
      setIsMarking(false); setProgressText("");
    }
  };

  return (
    <div className="p-6">
      <h1 className="mb-6 text-2xl font-bold">My Attendance</h1>
      <div className="mb-6 rounded bg-white p-4 shadow">
        {notice.type && <div className={`mb-4 rounded border p-4 ${notice.type === "success" ? "border-green-200 bg-green-50 text-green-800" : "border-red-200 bg-red-50 text-red-800"}`}><div className="font-semibold">{notice.type === "success" ? "✅ " : ""}{notice.title}</div><div className="mt-1 text-sm">{notice.message}</div>{notice.type === "error" && !todayRecord && <button type="button" onClick={() => setNotice(initialNotice)} className="mt-3 rounded border border-current px-3 py-1 text-sm font-semibold">Try Again</button>}</div>}

        {todayRecord ? (
          <div className="rounded bg-green-100 p-4 text-green-800">✅ Already marked: <strong>{todayRecord.status}</strong><br />Location: {todayRecord.location || todayRecord.officeName || "-"}{todayRecord.clientName && ` (Client: ${todayRecord.clientName})`}</div>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-[minmax(220px,1fr)_minmax(260px,1fr)_auto] md:items-end">
              <label className="text-sm font-medium">Attendance Location
                <select value={locationChoice} onChange={(e) => { setLocationChoice(e.target.value); setClientId(""); setNotice(initialNotice); }} className="mt-1 w-full rounded border px-3 py-2">
                  <option value="">Select location</option>
                  {settings.allowOffice !== false && activeOffices.map((office) => <option key={office.id} value={`office:${office.id}`}>{office.name}</option>)}
                  {settings.allowClient !== false && <option value="client">Client Visit</option>}
                </select>
              </label>
              {locationChoice === "client" ? <label className="text-sm font-medium">Client
                <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="mt-1 w-full rounded border px-3 py-2"><option value="">Select from Client Master</option>{activeClients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select>
              </label> : <div />}
              <button onClick={markAttendance} disabled={isMarking || Boolean(todayRecord)} className="rounded bg-blue-600 px-4 py-2 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">{isMarking ? progressText || "Please wait…" : "Mark Attendance"}</button>
            </div>
            {settings.geoAttendanceEnabled && <p className="text-xs text-gray-500">Location access will be requested only when you click Mark Attendance.</p>}
          </div>
        )}
      </div>

      <div className="rounded bg-white p-4 shadow"><h2 className="mb-3 text-lg font-semibold">My Monthly Log</h2><div className="overflow-x-auto"><table className="w-full border-collapse text-sm"><thead className="bg-gray-200 text-gray-700"><tr><th className="border px-4 py-2">Date</th><th className="border px-4 py-2">Status</th><th className="border px-4 py-2">Location</th><th className="border px-4 py-2">Client</th><th className="border px-4 py-2">Geo Status</th></tr></thead><tbody>{attendance.filter((item) => item.date?.startsWith(currentMonth)).map((item) => <tr key={item.id} className="hover:bg-gray-50"><td className="border px-4 py-2">{item.date}</td><td className={`border px-4 py-2 ${item.status === "Absent" ? "bg-red-100 text-red-700" : item.status === "Half Day" ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"}`}>{item.status}</td><td className="border px-4 py-2">{item.location || item.officeName || "-"}</td><td className="border px-4 py-2">{item.clientName || "-"}</td><td className="border px-4 py-2">{item.geoValidated ? `Verified (${item.distanceMeters ?? "-"} m)` : item.geoValidationNote || "-"}</td></tr>)}</tbody></table></div></div>
    </div>
  );
}
